import { buildParameterPickerCategories } from "components/CC/UI/ParameterPicker/buildParameterPickerCategories";
import { ParameterCategory, ParameterItem } from "components/CC/UI/ParameterPicker/ParameterPicker";

import FormStructureData = App.Data.FormStructureData;

/**
 * The same field can be part of several structures, so ids must be unique across the whole tree
 * (picker expansion state and React keys are keyed by id). `value` keeps the original id, so the
 * inserted tag and its validation stay identical to the flat CC "Form fields" category.
 */
const scopeItem = (item: ParameterItem, scope: string): ParameterItem => ({
    ...item,
    id: `${scope}|${item.id}`,
    value: item.value ?? item.id,
    children: item.children?.map((child) => scopeItem(child, scope)),
});

/**
 * "Form fields" picker category grouped by form structure: structure -> form fields.
 * Structures without fields are skipped. Returns null when no structure has any field.
 */
export const createFormStructureFieldsCategory = (
    formStructures: FormStructureData[],
    t: (key: string) => string
): ParameterCategory | null => {
    const structureItems: ParameterItem[] = [];
    let baseCategory: ParameterCategory | undefined;

    formStructures.forEach((structure) => {
        const [category] = buildParameterPickerCategories({
            t,
            allowedCategories: ["form_fields"],
            formFields: structure.formFields,
        });
        if (!category) {
            return;
        }

        baseCategory ??= category;
        const scope = `structure:${structure.id ?? structure.system_name}`;
        structureItems.push({
            id: scope,
            name: structure.name,
            paramType: "form_fields",
            children: category.items.map((item) => scopeItem(item, scope)),
        });
    });

    if (!baseCategory) {
        return null;
    }

    return { ...baseCategory, count: structureItems.length, items: structureItems };
};

/**
 * Cascade has one structure (from its integration), so its fields are shown directly. Offer can have several
 * (API structures and structures of its iframe forms), so those are grouped by structure.
 */
export const createFilterFieldsCategory = (
    formStructures: FormStructureData[],
    t: (key: string) => string
): ParameterCategory | null => {
    if (formStructures.length !== 1) {
        return createFormStructureFieldsCategory(formStructures, t);
    }

    const [category] = buildParameterPickerCategories({
        t,
        allowedCategories: ["form_fields"],
        formFields: formStructures[0].formFields,
    });
    return category ?? null;
};

/** Form fields of the offered structures by their filter tag (`data_<system_name>`), the first one wins. */
export const buildFormFieldsByTag = (formStructures: FormStructureData[]): Map<string, App.Data.FormFieldData> => {
    const map = new Map<string, App.Data.FormFieldData>();
    formStructures.forEach((structure) =>
        structure.formFields?.forEach((field) => {
            const tag = `data_${field.system_name}`;
            if (field.system_name && !map.has(tag)) {
                map.set(tag, field);
            }
        })
    );
    return map;
};
